import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Phone, Mail, MapPin, Send } from 'lucide-react';
import { useTenant } from '@/hooks/useTenant';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, FormEvent } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';

export function Footer() {
  const { config } = useTenant();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!config) return null;

  const currentYear = new Date().getFullYear();

  const socialIcons = {
    facebook: Facebook,
    instagram: Instagram,
    twitter: Twitter,
    linkedin: Linkedin,
  };

  const quickLinks = [
    { name: t('nav.sales'), href: '/ventas' },
    { name: t('nav.rentals'), href: '/arriendos' },
    { name: t('nav.services'), href: '/servicios' },
    { name: t('nav.about'), href: '/quienes-somos' },
    { name: t('nav.blog'), href: '/blog' },
    { name: t('nav.contact'), href: '/contacto' },
  ];

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!email || !email.trim()) {
      toast.error(t('newsletter.errors.emailRequired'));
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('newsletter.errors.emailInvalid'));
      return;
    }

    setIsSubmitting(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

      const response = await axios.post(`${apiUrl}/newsletter`, {
        email: email.toLowerCase().trim(),
        source: 'landing-footer',
      });

      if (response.data.success) {
        toast.success(t('newsletter.success'));
        setEmail(''); // Clear form
      } else {
        toast.error(t('newsletter.errors.subscribeError'));
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const message = error.response.data?.message || t('newsletter.errors.subscribeError');

        if (error.response.status === 409) {
          toast.error(t('newsletter.errors.emailExists'));
        } else {
          toast.error(message);
        }
      } else {
        toast.error(t('newsletter.errors.connectionError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div 
                className="w-8 h-8 rounded-md flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: config.primaryColor }}
              >
                {config.name.charAt(0)}
              </div>
              <span className="text-xl font-bold">
                {config.name}
              </span>
            </div>
            <p className="text-gray-400 mb-6 max-w-md">
              {t('footer.description')}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-gray-400" />
                <span className="text-gray-300">{config.contact.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <span className="text-gray-300">{config.contact.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <span className="text-gray-300">{config.contact.email}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.follow')}</h3>
            <div className="flex space-x-4 mb-6">
              {Object.entries(config.social).map(([platform, url]) => {
                if (!url) return null;
                const IconComponent = socialIcons[platform as keyof typeof socialIcons];
                if (!IconComponent) return null;

                const platformNames: Record<string, string> = {
                  facebook: t('socialMedia.facebook'),
                  instagram: t('socialMedia.instagram'),
                  twitter: t('socialMedia.twitter'),
                  linkedin: t('socialMedia.linkedin'),
                };

                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${t('footer.visitUs')} ${platformNames[platform] || platform}`}
                    className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
                    style={{
                      '--hover-color': config.primaryColor
                    } as React.CSSProperties}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = config.primaryColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                    }}
                  >
                    <IconComponent className="h-5 w-5" />
                  </a>
                );
              })}
            </div>

            {/* Newsletter Subscription Form */}
            <div>
              <h4 className="text-sm font-semibold mb-2">{t('footer.newsletter')}</h4>
              <p className="text-gray-400 text-sm mb-3">
                {t('footer.newsletterDescription')}
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.emailPlaceholder')}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 rounded-md bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    '--tw-ring-color': config.primaryColor
                  } as React.CSSProperties}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = config.primaryColor;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '';
                  }}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-md font-medium text-white transition-all duration-200 flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: config.primaryColor }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>{t('footer.subscribing')}</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>{t('footer.subscribe')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400">
              © {currentYear} {config.name}. {t('footer.rights')}.
            </p>
            <div className="flex items-center gap-4">
              <Link
                to="/accesibilidad"
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                {t('nav.accessibility')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}